/**
 * The Activity Center contract — https://app.notion.com/p/3c304e9a59288185892ef46b9483144f §4.
 *
 * ONE normalized record for every long-running thing Marcus does: indexing today, workflows and deep
 * research next. The panel renders from this shape and nothing else, which is the whole point — it
 * must not know what indexing is, or adding a second kind means editing the panel.
 *
 * The rule that keeps it that way: **anything domain-specific is resolved by the PRODUCER before it
 * reaches here.** `detail` arrives already localized; a quota block arrives as `state: 'attention'`
 * with an amber badge and a link action. The panel never learns what a quota is.
 */

/**
 * Lifecycle, deliberately coarse. Four states cover every producer we have or plan, and a fifth would
 * have to earn itself — every extra state is a branch in the one component allowed to render.
 *
 * `attention` is the load-bearing one: work that STOPPED and needs a human decision (quota exhausted,
 * a deferred read awaiting confirmation, a failure worth retrying). It sorts above everything.
 */
export type ActivityState = 'queued' | 'running' | 'attention' | 'done'

/**
 * Task families. Adding one is an entry in `ACTIVITY_KINDS` plus a producer — nothing else.
 *
 * `research` has no producer and has not had one since the deep-research feature was removed
 * (2026-07-24). It is left declared rather than deleted only because removing it is a separate
 * change from #56; nothing publishes it, so nothing renders it.
 */
export type ActivityKind =
  | 'indexing' | 'workflow' | 'research' | 'tool'
  | 'contract' | 'sheet' | 'ocr'
  /** The LLM gateway refused the organization's model calls (MAR-1009) — produced by `useUsageLimit`. */
  | 'usage'

/**
 * Tone is SEMANTIC, not a colour: the panel maps it. `warn` is amber and explicitly not red — a quota
 * block means nothing broke, and painting it as an error is the misreading this feature exists to end.
 *
 * `ask` is the same argument one step further (#84). A deferred read is not even a problem: nothing
 * stopped, nothing is owed, Marcus is holding a door open. Sharing `warn` with the quota block gave it
 * amber and a barred-circle glyph, so an invitation to read a document rendered as a small alarm.
 */
export type ActivityTone = 'work' | 'warn' | 'good' | 'bad' | 'ask'

/**
 * An action the panel can offer without knowing what it does.
 *
 * `link` navigates; `run` invokes a handler the PRODUCER owns. The handler lives with the producer so
 * domain logic (reprocess, cancel, confirm a read) never leaks into the store or the panel.
 */
export interface ActivityAction {
  id: string
  label: string
  kind: 'link' | 'run'
  /** For `kind: 'link'` — a router path. */
  to?: string
  variant?: 'primary' | 'default'
}

export interface ActivityTask {
  /** Unique across all kinds. Producers should namespace (`indexing:<itemId>`) to guarantee it. */
  id: string
  kind: ActivityKind
  title: string
  /** One line, ALREADY LOCALIZED by the producer — the store and panel never call `t()` on task data. */
  detail?: string
  state: ActivityState
  /** Ordered positions within the run. Never a percentage: our pipelines report positions, not fractions. */
  phases?: { label: string; done: boolean }[]
  badge?: { text: string; tone: ActivityTone }
  actions?: ActivityAction[]
  startedAt: number
  settledAt?: number
  /**
   * Acknowledged, and only visible because "mostrar descartados" is on.
   *
   * A CONTRACT EXTENSION rather than a panel special-case: dismissal is cross-kind (every kind carries
   * `activity_center_dismissed_at`), so the panel can dim a row and offer Restaurar without learning
   * what any kind is. The rule in §4 stands — if the panel ever needs to branch on `kind`, widen this
   * shape instead.
   */
  dismissed?: boolean
}

/** Presentation metadata per kind. The only shared file a new task family has to touch. */
export interface ActivityKindDescriptor {
  /** i18n key for the group heading / empty states. */
  labelKey: string
  /** lucide-vue-next icon name, resolved by the panel. */
  icon: string
}

export const ACTIVITY_KINDS: Record<ActivityKind, ActivityKindDescriptor> = {
  indexing: { labelKey: 'activity.kind.indexing', icon: 'FileText' },
  workflow: { labelKey: 'activity.kind.workflow', icon: 'Workflow' },
  research: { labelKey: 'activity.kind.research', icon: 'Search' },
  tool: { labelKey: 'activity.kind.tool', icon: 'Wrench' },
  contract: { labelKey: 'activity.kind.contract', icon: 'ShieldCheck' },
  sheet: { labelKey: 'activity.kind.sheet', icon: 'Table' },
  ocr: { labelKey: 'activity.kind.ocr', icon: 'ScanText' },
  usage: { labelKey: 'activity.kind.usage', icon: 'Gauge' },
}

/**
 * Sort order: **state before recency.**
 *
 * Ordering by time alone buries the one row that needed a decision under a busy day's finished work —
 * which is precisely how the quota block that prompted this feature went unnoticed.
 */
export const STATE_ORDER: Record<ActivityState, number> = {
  attention: 0,
  running: 1,
  queued: 2,
  done: 3,
}

/** Still in flight — the panel shows a spinner and the poller keeps asking. */
export function isActive(state: ActivityState): boolean {
  return state === 'running' || state === 'queued'
}

/** Finished or parked: safe to clear, and counted in the "listos hoy" receipt. */
export function isSettled(state: ActivityState): boolean {
  return state === 'done'
}
